import { computed } from 'vue'
import type { ProjectsState } from './state'
import type { Project, ProjectStatus } from '@/types'

export const createGetters = (state: ProjectsState) => {
  const filteredProjects = computed(() => {
    let result = [...state.projects]

    // Filter by search
    if (state.filters.search) {
      const search = state.filters.search.toLowerCase()
      result = result.filter(
        (project) =>
          project.name.toLowerCase().includes(search) ||
          project.description?.toLowerCase().includes(search)
      )
    }

    // Filter by status
    if (state.filters.status) {
      result = result.filter((project) => project.status === state.filters.status)
    }

    // Filter by organization
    if (state.filters.organizationId) {
      result = result.filter((project) => project.organizationId === state.filters.organizationId)
    }

    return result
  })

  const projectsByStatus = computed(() => {
    const grouped: Record<ProjectStatus, Project[]> = {
      definition: [],
      in_development: [],
      qa: [],
      uat: [],
      production: [],
      maintenance: [],
      completed: [],
      on_hold: [],
    }

    state.projects.forEach((project) => {
      if (grouped[project.status]) {
        grouped[project.status].push(project)
      }
    })

    return grouped
  })

  const activeProjects = computed(() => {
    return state.projects.filter(
      (project) =>
        project.status !== 'completed' && project.status !== 'on_hold'
    )
  })

  const projectsByOrganization = computed(() => {
    const grouped: Record<string, Project[]> = {}

    filteredProjects.value.forEach((project) => {
      if (!grouped[project.organizationId]) {
        grouped[project.organizationId] = []
      }
      grouped[project.organizationId].push(project)
    })

    return grouped
  })

  const stats = computed(() => {
    const total = state.projects.length
    const active = activeProjects.value.length
    const inDevelopment = state.projects.filter((p) => p.status === 'in_development').length
    const inQA = state.projects.filter((p) => p.status === 'qa' || p.status === 'uat').length
    const completed = state.projects.filter((p) => p.status === 'completed').length
    const onHold = state.projects.filter((p) => p.status === 'on_hold').length

    return {
      total,
      active,
      inDevelopment,
      inQA,
      completed,
      onHold,
    }
  })

  const getProjectById = (id: string): Project | undefined => {
    return state.projects.find((project) => project.id === id)
  }

  const getProjectsByOrganizationId = (orgId: string): Project[] => {
    return state.projects.filter((project) => project.organizationId === orgId)
  }

  return {
    filteredProjects,
    projectsByStatus,
    activeProjects,
    projectsByOrganization,
    stats,
    getProjectById,
    getProjectsByOrganizationId,
  }
}
